/**
 * Argus — file push pipeline: DnD upload din Win Explorer → operator UI → agent.
 *
 * Bloc file-push-deployment 2026-05-07.
 *
 * Flow per fișier:
 *   1. wsClient.requestFilePushInit({localId, targetPath, fileName, sizeBytes}) → ack ok
 *   2. Read file chunks 1 MB (FileReader.readAsArrayBuffer slice) base64 encode
 *   3. wsClient.requestFilePushChunk({localId, offset, data, eof}) loop sequential
 *   4. eof=true → agent rename atomic + emite WS `file_pushed` broadcast
 *
 * Multiple files: queue cu concurrency limit 2 paralele.
 * Folders recursive: enumerateDataTransferEntries walk via webkitGetAsEntry.
 */

import { wsClient } from './wsClient';

export const CHUNK_BYTES = 1 * 1024 * 1024;          // 1 MB per chunk
export const MAX_BYTES_PER_FILE = 100 * 1024 * 1024; // 100 MB hard cap (mirror agent)
export const PARALLEL_FILES = 2;

export type PushItemStatus = 'pending' | 'uploading' | 'done' | 'failed' | 'cancelled';

export interface PushItem {
  localId: string;
  file: File;
  relativePath: string;       // ex "myfolder/sub/img.jpg" — joinPath cu currentPath = targetPath
  targetPath: string;         // absolute — ex "C:\\Users\\...\\myfolder\\sub\\img.jpg"
  size: number;
  bytesUploaded: number;
  status: PushItemStatus;
  error?: string;
  startedAt?: number;
  completedAt?: number;
}

export interface DropEntry {
  file: File;
  relativePath: string;
}

/**
 * Walk DataTransferItemList recursive via webkitGetAsEntry. Return flat list cu relative paths.
 * Folosit pentru DnD selecție mixtă (file + folder).
 */
export async function enumerateDataTransferEntries(items: DataTransferItemList): Promise<DropEntry[]> {
  const out: DropEntry[] = [];

  async function walk(entry: FileSystemEntry, prefix: string): Promise<void> {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await new Promise<File>((res, rej) => fileEntry.file(res, rej));
      out.push({ file, relativePath: prefix + file.name });
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const reader = dirEntry.createReader();
      // readEntries returnează batches până la exhausting → loop until empty
      let batch: FileSystemEntry[];
      do {
        batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        for (const child of batch) {
          await walk(child, prefix + dirEntry.name + '/');
        }
      } while (batch.length > 0);
    }
  }

  const walks: Promise<void>[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;
    const entry = item.webkitGetAsEntry();
    if (entry) walks.push(walk(entry, ''));
  }
  await Promise.all(walks);
  return out;
}

/**
 * Fallback pentru browsers fără webkitGetAsEntry — folosește FileList plat (no folders).
 * Edge case rar (toate browser modern suportă webkitGetAsEntry de facto).
 */
export function fileListToDropEntries(files: FileList): DropEntry[] {
  const out: DropEntry[] = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    if (f) out.push({ file: f, relativePath: f.name });
  }
  return out;
}

function joinAgentPath(base: string, rel: string): string {
  // base e Win path (X:\Users\... sau \\srv\share\...). rel e POSIX (/-separator) din DataTransferEntries.
  const baseWin = base.replace(/\//g, '\\').replace(/\\+$/, '');
  const relWin = rel.replace(/\//g, '\\').replace(/^\\+/, '');
  return baseWin + '\\' + relWin;
}

function uuidV4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
  }
  // Fallback (non-secure) pentru context older — Vite dev e https-able peste localhost.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 32768;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

async function readFileChunk(file: File, offset: number, size: number): Promise<ArrayBuffer> {
  const slice = file.slice(offset, offset + size);
  return await slice.arrayBuffer();
}

/**
 * Push pipeline — queue cu concurrency limit 2 paralele (max throughput vs predictabilitate UI).
 * onProgress invoked la fiecare update (init ack, chunk ack, completion, failure).
 * signal: AbortController.signal pentru cancel global.
 */
export async function pushFiles(
  agentId: string,
  currentPath: string,
  drops: DropEntry[],
  onProgress: (items: PushItem[]) => void,
  signal: AbortSignal,
): Promise<PushItem[]> {
  const items: PushItem[] = drops.map((d) => ({
    localId: uuidV4(),
    file: d.file,
    relativePath: d.relativePath,
    targetPath: joinAgentPath(currentPath, d.relativePath),
    size: d.file.size,
    bytesUploaded: 0,
    status: 'pending' as PushItemStatus,
  }));

  // Filter out files >100 MB ahead of time → mark failed.
  for (const it of items) {
    if (it.size > MAX_BYTES_PER_FILE) {
      it.status = 'failed';
      it.error = `Fișier prea mare (${Math.round(it.size / 1024 / 1024)} MB > ${MAX_BYTES_PER_FILE / 1024 / 1024} MB)`;
    }
  }

  onProgress([...items]);

  const queue = items.filter((i) => i.status === 'pending');
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < queue.length) {
      if (signal.aborted) return;
      const item = queue[cursor++];
      if (!item) continue;
      try {
        item.status = 'uploading';
        item.startedAt = Date.now();
        onProgress([...items]);

        // 1. Init.
        await wsClient.requestFilePushInit(agentId, {
          localId: item.localId,
          targetPath: item.targetPath,
          fileName: item.file.name,
          sizeBytes: item.size,
        });

        // 2. Chunk loop.
        let offset = 0;
        while (offset < item.size) {
          if (signal.aborted) {
            item.status = 'cancelled';
            onProgress([...items]);
            return;
          }
          const chunkSize = Math.min(CHUNK_BYTES, item.size - offset);
          const buf = await readFileChunk(item.file, offset, chunkSize);
          const data = arrayBufferToBase64(buf);
          const eof = offset + chunkSize >= item.size;
          await wsClient.requestFilePushChunk(agentId, {
            localId: item.localId,
            offset,
            data,
            eof,
          });
          offset += chunkSize;
          item.bytesUploaded = offset;
          onProgress([...items]);
        }

        item.status = 'done';
        item.completedAt = Date.now();
        onProgress([...items]);
      } catch (err) {
        item.status = 'failed';
        item.error = err instanceof Error ? err.message : String(err);
        item.completedAt = Date.now();
        onProgress([...items]);
      }
    }
  }

  // Start N workers paralele. queue acts as shared FIFO via cursor.
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(PARALLEL_FILES, queue.length); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);
  return items;
}
